import { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../../api/client';

interface ZoneInfo { id: string; name: string; colorCode?: string; tables: number; seats: number; occupied: number; available: number; emptyCount: number; }
interface EmptyTable { id: string; tableNumber: number; tableName: string; zoneName: string; zoneId: string; capacity: number; }

export function TableTransferPanel() {
  const [zones, setZones] = useState<ZoneInfo[]>([]);
  const [emptyTables, setEmptyTables] = useState<EmptyTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState<EmptyTable | null>(null);
  const [targetZoneId, setTargetZoneId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [success, setSuccess] = useState('');
  const [collapsedZones, setCollapsedZones] = useState<Set<string>>(new Set());

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const zoneData = await apiClient<any[]>('/table-management/zones');
      const tableData = await apiClient<any>('/table-management/tables');
      const allTables = Array.isArray(tableData) ? tableData : (tableData.tables || []);

      const allEmpty: EmptyTable[] = [];
      const zoneStatsMap = new Map<string, { tables: number; seats: number; occupied: number; emptyCount: number }>();
      const zoneColorMap = new Map<string, string>();

      for (const z of zoneData) {
        zoneColorMap.set(z.id, z.colorCode || '#1a6b3c');
      }

      for (const t of allTables) {
        const zId = t.zoneId || t.zone?.id;
        const zName = t.zone?.name || t.zoneName || '';
        const seats = t.seats || [];
        const totalSeats = seats.length || t.capacity || 10;
        const occupiedSeats = seats.filter((s: any) => s.status === 'Assigned' || s.status === 'CheckedIn').length;

        if (!zoneStatsMap.has(zId)) zoneStatsMap.set(zId, { tables: 0, seats: 0, occupied: 0, emptyCount: 0 });
        const zs = zoneStatsMap.get(zId)!;
        zs.tables++; zs.seats += totalSeats; zs.occupied += occupiedSeats;

        if (occupiedSeats === 0 && totalSeats > 0) {
          zs.emptyCount++;
          allEmpty.push({ id: t.id, tableNumber: t.tableNumber, tableName: t.tableName || `Table ${t.tableNumber}`, zoneName: zName, zoneId: zId, capacity: totalSeats });
        }
      }

      const allZones: ZoneInfo[] = [];
      for (const z of zoneData) {
        const stats = zoneStatsMap.get(z.id) || { tables: 0, seats: 0, occupied: 0, emptyCount: 0 };
        allZones.push({ id: z.id, name: z.name, colorCode: zoneColorMap.get(z.id), tables: stats.tables, seats: stats.seats, occupied: stats.occupied, available: stats.seats - stats.occupied, emptyCount: stats.emptyCount });
      }

      setZones(allZones);
      setEmptyTables(allEmpty.sort((a, b) => a.tableNumber - b.tableNumber));
    } catch {}
    finally { setLoading(false); }
  }

  // Group empty tables by zone
  const tablesByZone = useMemo(() => {
    const grouped = new Map<string, EmptyTable[]>();
    for (const t of emptyTables) {
      if (!grouped.has(t.zoneId)) grouped.set(t.zoneId, []);
      grouped.get(t.zoneId)!.push(t);
    }
    // Sort zones by name using the zones array order
    const ordered: { zone: ZoneInfo; tables: EmptyTable[] }[] = [];
    for (const z of zones) {
      const tables = grouped.get(z.id);
      if (tables && tables.length > 0) {
        ordered.push({ zone: z, tables: tables.sort((a, b) => a.tableNumber - b.tableNumber) });
      }
    }
    return ordered;
  }, [emptyTables, zones]);

  function toggleZoneCollapse(zoneId: string) {
    setCollapsedZones(prev => {
      const next = new Set(prev);
      if (next.has(zoneId)) next.delete(zoneId);
      else next.add(zoneId);
      return next;
    });
  }

  async function handleConfirm() {
    if (!selectedTable || !targetZoneId) return;
    setConfirming(true);
    try {
      await apiClient(`/table-management/tables/${selectedTable.id}`, { method: 'PATCH', body: { zoneId: targetZoneId } });
      setSuccess(`Table ${selectedTable.tableNumber} moved to ${zones.find(z => z.id === targetZoneId)?.name}`);
      setSelectedTable(null); setTargetZoneId('');
      fetchData();
      setTimeout(() => setSuccess(''), 4000);
    } catch { alert('Transfer failed'); }
    finally { setConfirming(false); }
  }

  const sourceZone = selectedTable ? zones.find(z => z.id === selectedTable.zoneId) : null;
  const targetZone = targetZoneId ? zones.find(z => z.id === targetZoneId) : null;

  // Available target zones (exclude source zone)
  const targetZones = useMemo(() => {
    if (!selectedTable) return [];
    return zones.filter(z => z.id !== selectedTable.zoneId);
  }, [zones, selectedTable]);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Loading tables...</div>;

  return (
    <div>
      <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: '#0f2d1f' }}>🔄 Transfer Empty Table to Another Zone</h3>
      {success && <div style={{ padding: '8px 12px', backgroundColor: '#dcfce7', borderRadius: '6px', fontSize: '0.85rem', color: '#166534', marginBottom: '12px' }}>✓ {success}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', minHeight: '350px' }}>
        {/* PANE 1: Empty Tables grouped by Zone */}
        <div style={{ borderRight: '1px solid #e5e7eb', padding: '12px', backgroundColor: '#f9fafb' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#555', textTransform: 'uppercase', marginBottom: '8px' }}>Empty Tables ({emptyTables.length})</div>
          {emptyTables.length === 0 ? <div style={{ color: '#999', fontSize: '0.8rem' }}>No completely empty tables found.</div> : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {tablesByZone.map(({ zone, tables }) => {
                const isCollapsed = collapsedZones.has(zone.id);
                return (
                  <div key={zone.id} style={{ marginBottom: '8px' }}>
                    {/* Zone group header */}
                    <button
                      onClick={() => toggleZoneCollapse(zone.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', padding: '6px 8px', border: 'none', borderRadius: '4px', backgroundColor: '#e8eceb', cursor: 'pointer', textAlign: 'left' }}
                      aria-expanded={!isCollapsed}
                      aria-label={`${zone.name} zone, ${tables.length} empty tables`}
                    >
                      <span style={{ fontSize: '0.7rem', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block' }}>▼</span>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: zone.colorCode || '#1a6b3c', flexShrink: 0 }} />
                      <span style={{ fontWeight: 600, fontSize: '0.75rem', color: '#333', flex: 1 }}>{zone.name}</span>
                      <span style={{ fontSize: '0.68rem', color: '#666', backgroundColor: '#fff', borderRadius: '8px', padding: '1px 6px' }}>{tables.length}</span>
                    </button>
                    {/* Zone tables */}
                    {!isCollapsed && (
                      <div style={{ paddingLeft: '8px', marginTop: '4px' }}>
                        {tables.map(t => (
                          <div key={t.id} onClick={() => { setSelectedTable(t); setTargetZoneId(''); }}
                            style={{ padding: '7px 8px', marginBottom: '3px', borderRadius: '6px', cursor: 'pointer', border: selectedTable?.id === t.id ? '2px solid #1a6b3c' : '1px solid #e5e7eb', backgroundColor: selectedTable?.id === t.id ? '#e8f5e9' : '#fff', transition: 'border-color 0.15s' }}>
                            <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>Table {t.tableNumber}</div>
                            <div style={{ fontSize: '0.68rem', color: '#666' }}>{t.capacity} seats</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* PANE 2: Target Zone (grouped dropdown) */}
        <div style={{ borderRight: '1px solid #e5e7eb', padding: '12px' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#555', textTransform: 'uppercase', marginBottom: '8px' }}>Move To Zone</div>
          {!selectedTable ? <div style={{ color: '#999', fontSize: '0.8rem' }}>Select a table from the left first.</div> : (
            <div>
              <div style={{ fontSize: '0.72rem', color: '#666', marginBottom: '6px' }}>
                Moving <strong>Table {selectedTable.tableNumber}</strong> from <strong>{selectedTable.zoneName}</strong>
              </div>
              <select
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.85rem', marginBottom: '12px' }}
                value={targetZoneId}
                onChange={e => setTargetZoneId(e.target.value)}
                aria-label="Select target zone"
              >
                <option value="">— Select target zone —</option>
                {targetZones.map(z => (
                  <option key={z.id} value={z.id}>
                    {z.name} — {z.available} avail / {z.seats} total ({z.tables} tables)
                  </option>
                ))}
              </select>

              {/* Visual zone cards as alternative quick-select */}
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {targetZones.map(z => (
                  <div
                    key={z.id}
                    onClick={() => setTargetZoneId(z.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', marginBottom: '4px', borderRadius: '6px', cursor: 'pointer', border: targetZoneId === z.id ? '2px solid #1a6b3c' : '1px solid #e5e7eb', backgroundColor: targetZoneId === z.id ? '#e8f5e9' : '#fff', transition: 'border-color 0.15s' }}
                    role="option"
                    aria-selected={targetZoneId === z.id}
                  >
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: z.colorCode || '#1a6b3c', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>{z.name}</div>
                      <div style={{ fontSize: '0.68rem', color: '#666' }}>{z.available} seats avail • {z.tables} tables</div>
                    </div>
                  </div>
                ))}
              </div>

              {targetZoneId && (
                <button onClick={handleConfirm} disabled={confirming} style={{ width: '100%', padding: '10px', marginTop: '10px', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                  {confirming ? 'Moving...' : '✓ Confirm Move'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* PANE 3: Before/After Stats */}
        <div style={{ padding: '12px', backgroundColor: '#fafbfc' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#555', textTransform: 'uppercase', marginBottom: '8px' }}>Impact Preview</div>
          {(!selectedTable || !targetZone) ? <div style={{ color: '#999', fontSize: '0.8rem' }}>Select table and target zone to see impact.</div> : (
            <div style={{ fontSize: '0.8rem' }}>
              <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca' }}>
                <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>Source: {sourceZone?.name} (loses table)</div>
                <div>Tables: {sourceZone?.tables} → {(sourceZone?.tables || 0) - 1}</div>
                <div>Total Seats: {sourceZone?.seats} → {(sourceZone?.seats || 0) - (selectedTable?.capacity || 0)}</div>
                <div>Available: {sourceZone?.available} → {(sourceZone?.available || 0) - (selectedTable?.capacity || 0)}</div>
              </div>
              <div style={{ padding: '8px', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontWeight: 600, color: '#166534', marginBottom: '4px' }}>Target: {targetZone.name} (gains table)</div>
                <div>Tables: {targetZone.tables} → {targetZone.tables + 1}</div>
                <div>Total Seats: {targetZone.seats} → {targetZone.seats + (selectedTable?.capacity || 0)}</div>
                <div>Available: {targetZone.available} → {targetZone.available + (selectedTable?.capacity || 0)}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
